import { ToolItem, RegionInfo, CurrencyMetadata, ContentEntry } from '../../types';
import { TOOLS_DIRECTORY } from '../../data/toolsData';
import { REGIONS } from '../../data/regionsData';
import { GLOBAL_CURRENCIES_LIST } from '../../data/fxCurrenciesData';
import { SAMPLE_GUIDES } from '../../data/seoCmsData';
import { API_PRODUCTS } from '../../data/developerMockData';

export type SearchCategoryGroup =
  | 'Tools'
  | 'Currencies'
  | 'Guides'
  | 'Countries / Markets'
  | 'Help'
  | 'Developer Documentation';

export interface SearchResultItem {
  id: string;
  group: SearchCategoryGroup;
  title: string;
  subtitle: string;
  categoryTag?: string;
  iconName: string;
  badgeText?: string;
  badgeType?: 'primary' | 'success' | 'warning' | 'info' | 'neutral';
  meta?: string;
  score: number;
  matchReason: 'exact' | 'alias' | 'category' | 'popularity' | 'market relevance' | 'semantic';
  action: {
    type: 'tool' | 'view' | 'guide' | 'currency' | 'region' | 'api' | 'external';
    targetId: string;
    payload?: any;
  };
}

export interface HelpTopicItem {
  id: string;
  title: string;
  summary: string;
  category: string;
  keywords: string[];
  viewTarget: string;
}

export const HELP_TOPICS: HelpTopicItem[] = [
  {
    id: 'help_calc_accuracy',
    title: 'How does YamooPay guarantee calculation accuracy?',
    summary: 'Our calculation engines use high-precision BigInt arithmetic with official formulas from IRS, HMRC, DGFiP, and ECB data.',
    category: 'Accuracy & Methodology',
    keywords: ['accuracy', 'precision', 'rounding', 'formula', 'methodology', 'reliable', 'irs', 'hmrc', 'verified'],
    viewTarget: 'methodology'
  },
  {
    id: 'help_pwa_offline',
    title: 'Using YamooPay offline & installing the mobile PWA',
    summary: 'Install YamooPay as a Progressive Web App on iOS & Android for zero-connectivity offline calculations.',
    category: 'Mobile & Offline',
    keywords: ['offline', 'pwa', 'install', 'mobile', 'ios', 'android', 'no internet', 'cache', 'airplane'],
    viewTarget: 'help-center'
  },
  {
    id: 'help_passkey_privacy',
    title: 'Zero-Password login & client-side encryption privacy',
    summary: 'YamooPay uses biometric passkeys and encrypted local storage. We never sell your financial figures.',
    category: 'Privacy & Security',
    keywords: ['privacy', 'passkey', 'security', 'login', 'data', 'gdpr', 'confidential', 'cookies'],
    viewTarget: 'data-governance'
  },
  {
    id: 'help_money_transfers',
    title: 'Comparing international money transfer rates & fees',
    summary: 'Compare live real-time rates across Wise, Western Union, Remitly, Moneroo, and bank wires with transparent FX markup.',
    category: 'Money Transfers',
    keywords: ['remittance', 'send money', 'fees', 'transfer', 'wise', 'remitly', 'western union', 'markup', 'swift'],
    viewTarget: 'money-transfers'
  },
  {
    id: 'help_monetization_terms',
    title: 'Billing, enterprise subscriptions & commercial licensing',
    summary: 'Review self-serve YamooPay Pro, metered developer API usage tiers, SLA terms, and VAT/TVA tax receipts.',
    category: 'Billing & Commercial',
    keywords: ['billing', 'invoice', 'subscription', 'price', 'enterprise', 'commercial', 'receipt', 'tax invoice'],
    viewTarget: 'billing-command-center'
  },
  {
    id: 'help_fx_spreads',
    title: 'Mid-Market FX rates vs retail bank markups explained',
    summary: 'Learn why institutional mid-market rates differ from retail exchange booth spreads and card transaction fees.',
    category: 'Currency & FX',
    keywords: ['fx spread', 'mid market', 'exchange rate', 'markup', 'hidden fee', 'bank fee', 'currency markup'],
    viewTarget: 'currency-intelligence'
  }
];

export interface DeveloperDocItem {
  id: string;
  title: string;
  summary: string;
  endpoint: string;
  method: 'GET' | 'POST';
  slug: string;
  keywords: string[];
}

export const DEVELOPER_DOC_ITEMS: DeveloperDocItem[] = [
  {
    id: 'doc_fx_v1_rates',
    title: 'FX Rates Real-Time Streaming & Conversion Endpoint',
    summary: 'Query live mid-market exchange rates, historical ticks, and multi-currency matrix endpoints.',
    endpoint: 'GET /api/v1/fx/rates',
    method: 'GET',
    slug: 'currency-fx',
    keywords: ['api', 'fx', 'rates', 'currency', 'rest', 'json', 'endpoints', 'rates endpoint', 'streaming']
  },
  {
    id: 'doc_salary_v1_calculate',
    title: 'Salary & Payroll Tax Calculation API',
    summary: 'Automate gross-to-net paycheck calculations across 35+ global jurisdictions with full bracket breakdown.',
    endpoint: 'POST /api/v1/salary/calculate',
    method: 'POST',
    slug: 'salary-payroll-tax',
    keywords: ['api', 'salary', 'payroll', 'tax api', 'gross net', 'withholding', 'developer api']
  },
  {
    id: 'doc_loan_v1_amortization',
    title: 'Mortgage & Loan Amortization Schedule API',
    summary: 'Generate monthly/annual principal & interest amortization schedules with early payment simulations.',
    endpoint: 'POST /api/v1/lending/amortization',
    method: 'POST',
    slug: 'loan-mortgage',
    keywords: ['api', 'mortgage', 'loan', 'amortization', 'schedule', 'interest', 'developer']
  },
  {
    id: 'doc_webhooks_v1',
    title: 'Webhooks & Rate Alert Notification Delivery',
    summary: 'Configure signed HMAC SHA256 webhooks for currency rate alerts and regulatory tax table change events.',
    endpoint: 'POST /api/v1/webhooks',
    method: 'POST',
    slug: 'webhooks',
    keywords: ['webhook', 'hmac', 'notifications', 'rate alert', 'signature', 'events']
  },
  {
    id: 'doc_widget_embed',
    title: 'Embeddable FX & Salary Calculator Widget SDK',
    summary: 'Embed responsive, white-label, zero-iframe financial calculation widgets on any merchant website.',
    endpoint: 'SDK <yamoo-calculator>',
    method: 'GET',
    slug: 'widgets',
    keywords: ['widget', 'sdk', 'embed', 'iframe', 'web component', 'white label']
  }
];

export class PublicSearchEngine {
  private tools: ToolItem[];
  private regions: RegionInfo[];
  private currencies: CurrencyMetadata[];
  private guides: ContentEntry[];

  constructor(
    tools: ToolItem[] = TOOLS_DIRECTORY,
    regions: RegionInfo[] = REGIONS,
    currencies: CurrencyMetadata[] = GLOBAL_CURRENCIES_LIST,
    guides: ContentEntry[] = SAMPLE_GUIDES
  ) {
    this.tools = tools;
    this.regions = regions;
    this.currencies = currencies;
    this.guides = guides;
  }

  /**
   * Deterministic Ranker:
   * 1. Exact title / code / keyword match
   * 2. Alias / synonym match
   * 3. Category match
   * 4. Popularity / Featured priority
   * 5. Current Market / Region relevance
   * Optional: AI Semantic enhancement flag
   */
  public search(
    rawQuery: string,
    currentRegionCode: string = 'US',
    filterGroup: SearchCategoryGroup | 'ALL' = 'ALL'
  ): {
    resultsByGroup: Record<SearchCategoryGroup, SearchResultItem[]>;
    flatRankedResults: SearchResultItem[];
    totalCount: number;
    hasQuery: boolean;
    directAnswer?: {
      type: 'fx_quick_convert' | 'percentage' | 'salary_estimate' | 'tip_split';
      title: string;
      value: string;
      detail: string;
      actionToolId: string;
    };
  } {
    const q = rawQuery.trim().toLowerCase();
    const hasQuery = q.length > 0;

    const allResults: SearchResultItem[] = [];

    // 1. EVALUATE DIRECT INSTANT CALCULATIONS (e.g., "100 USD to EUR", "15% of 250", "$80k in UK")
    let directAnswer: any = undefined;
    if (hasQuery) {
      directAnswer = this.evaluateDirectFormula(q, currentRegionCode);
    }

    // 2. SEARCH TOOLS
    this.tools.forEach((tool) => {
      const name = tool.name.toLowerCase();
      const label = (tool.label || '').toLowerCase();
      const desc = (tool.description || '').toLowerCase();
      const cat = (tool.category || '').toLowerCase();
      const tagline = (tool.tagline || '').toLowerCase();
      const slug = (tool.slug || '').toLowerCase();

      let score = 0;
      let matchReason: SearchResultItem['matchReason'] = 'category';

      if (!hasQuery) {
        // Default ranking: Popular tools first
        if (tool.popular) score += 80;
        if (tool.featured) score += 30;
        matchReason = 'popularity';
      } else {
        // 1. Exact Name/Slug Match
        if (name === q || slug === q || label === q) {
          score += 1000;
          matchReason = 'exact';
        } else if (name.startsWith(q) || slug.startsWith(q)) {
          score += 500;
          matchReason = 'exact';
        } else if (name.includes(q)) {
          score += 300;
          matchReason = 'exact';
        }

        // 2. Intent Aliases & Synonyms
        const aliases = this.getToolAliases(tool.id);
        const matchedAlias = aliases.find((a) => a === q || q.includes(a) || a.includes(q));
        if (matchedAlias) {
          score += 250;
          if (score < 400) matchReason = 'alias';
        }

        // 3. Category Match
        if (cat.includes(q) || tagline.includes(q)) {
          score += 120;
          if (score < 200) matchReason = 'category';
        }

        // 4. Description Content
        if (desc.includes(q)) {
          score += 80;
        }

        // 5. Popularity Boost
        if (tool.popular) score += 40;
        if (tool.featured) score += 20;

        // 6. Market / Jurisdiction Relevance
        if (
          tool.regionsSupported?.includes('Global') ||
          tool.regionsSupported?.includes(currentRegionCode) ||
          tool.marketSupport?.includes(currentRegionCode)
        ) {
          score += 30;
          if (score < 100) matchReason = 'market relevance';
        }
      }

      if (score > 0 || !hasQuery) {
        allResults.push({
          id: `tool_${tool.id}`,
          group: 'Tools',
          title: tool.name,
          subtitle: tool.tagline || tool.description || `${tool.category} calculation tool`,
          categoryTag: tool.category,
          iconName: tool.iconName || 'Calculator',
          badgeText: tool.popular ? 'Popular' : undefined,
          badgeType: tool.popular ? 'primary' : 'neutral',
          score,
          matchReason,
          action: {
            type: 'tool',
            targetId: tool.id
          }
        });
      }
    });

    // 3. SEARCH CURRENCIES
    this.currencies.forEach((curr) => {
      const code = curr.code.toLowerCase();
      const name = curr.name.toLowerCase();
      const locName = (curr.localizedName || '').toLowerCase();
      const symbol = curr.symbol.toLowerCase();
      const countries = (curr.countries || []).map((c) => c.toLowerCase());
      const aliases = (curr.aliases || []).map((a) => a.toLowerCase());

      let score = 0;
      let matchReason: SearchResultItem['matchReason'] = 'category';

      if (!hasQuery) {
        if (curr.isPopular) score += 70;
        if (curr.isMajor) score += 40;
        matchReason = 'popularity';
      } else {
        // 1. Exact Code or Symbol Match
        if (code === q || symbol === q) {
          score += 900;
          matchReason = 'exact';
        } else if (code.startsWith(q)) {
          score += 450;
          matchReason = 'exact';
        } else if (name === q) {
          score += 400;
          matchReason = 'exact';
        }

        // 2. Alias / Common Slang (e.g. buck, greenback, pound, quid, euro)
        if (aliases.some((a) => a === q || q.includes(a))) {
          score += 260;
          if (score < 400) matchReason = 'alias';
        }

        // 3. Country / Authority
        if (countries.some((c) => c.includes(q)) || name.includes(q) || locName.includes(q)) {
          score += 150;
          if (score < 200) matchReason = 'category';
        }

        // 4. Popularity
        if (curr.isPopular) score += 50;
        if (curr.isMajor) score += 25;

        // 5. Market match
        if (curr.countries.some((c) => c.toUpperCase().includes(currentRegionCode))) {
          score += 35;
        }
      }

      if (score > 0 || !hasQuery) {
        allResults.push({
          id: `curr_${curr.code}`,
          group: 'Currencies',
          title: `${curr.code} — ${curr.name}`,
          subtitle: `${curr.flagEmoji || '🌐'} ${curr.symbol} • ${curr.monetaryAuthority} • ${curr.countries.slice(0, 2).join(', ')}`,
          categoryTag: curr.category,
          iconName: 'Coins',
          badgeText: curr.isMajor ? 'Major' : undefined,
          badgeType: 'neutral',
          score,
          matchReason,
          action: {
            type: 'currency',
            targetId: curr.code,
            payload: curr
          }
        });
      }
    });

    // 4. SEARCH GUIDES & EDITORIAL ARTICLES
    this.guides.forEach((guide) => {
      const title = guide.title.toLowerCase();
      const summary = guide.summary.toLowerCase();
      const slug = guide.slug.toLowerCase();

      let score = 0;
      let matchReason: SearchResultItem['matchReason'] = 'category';

      if (!hasQuery) {
        score += 50;
        matchReason = 'popularity';
      } else {
        if (title === q || slug === q) {
          score += 800;
          matchReason = 'exact';
        } else if (title.includes(q)) {
          score += 320;
          matchReason = 'exact';
        } else if (summary.includes(q)) {
          score += 140;
          matchReason = 'category';
        }

        if (guide.market === 'GLOBAL' || guide.market === currentRegionCode) {
          score += 30;
          matchReason = 'market relevance';
        }
      }

      if (score > 0 || !hasQuery) {
        allResults.push({
          id: `guide_${guide.id}`,
          group: 'Guides',
          title: guide.title,
          subtitle: guide.summary,
          categoryTag: 'YMYL Verified Guide',
          iconName: 'BookOpen',
          badgeText: 'Verified E-E-A-T',
          badgeType: 'success',
          score,
          matchReason,
          action: {
            type: 'guide',
            targetId: guide.slug,
            payload: guide
          }
        });
      }
    });

    // 5. SEARCH COUNTRIES / MARKETS
    this.regions.forEach((reg) => {
      const name = reg.name.toLowerCase();
      const code = reg.code.toLowerCase();
      const curr = reg.currency.toLowerCase();
      const taxYear = (reg.taxYear || '').toLowerCase();

      let score = 0;
      let matchReason: SearchResultItem['matchReason'] = 'category';

      if (!hasQuery) {
        if (reg.code === currentRegionCode) score += 90;
        else score += 40;
        matchReason = 'market relevance';
      } else {
        if (code === q || name === q) {
          score += 850;
          matchReason = 'exact';
        } else if (name.startsWith(q) || code.startsWith(q)) {
          score += 420;
          matchReason = 'exact';
        } else if (name.includes(q) || curr.includes(q) || taxYear.includes(q)) {
          score += 180;
          matchReason = 'category';
        }

        if (reg.code === currentRegionCode) {
          score += 50;
          matchReason = 'market relevance';
        }
      }

      if (score > 0 || !hasQuery) {
        allResults.push({
          id: `market_${reg.code}`,
          group: 'Countries / Markets',
          title: `${reg.flag} ${reg.name} (${reg.code})`,
          subtitle: `Official Currency: ${reg.currency} (${reg.currencySymbol}) • ${reg.taxYear} • VAT: ${reg.standardVatRate}%`,
          categoryTag: 'Jurisdiction',
          iconName: 'Globe',
          badgeText: reg.code === currentRegionCode ? 'Active Region' : reg.currency,
          badgeType: reg.code === currentRegionCode ? 'primary' : 'neutral',
          score,
          matchReason,
          action: {
            type: 'region',
            targetId: reg.code,
            payload: reg
          }
        });
      }
    });

    // 6. SEARCH HELP TOPICS
    HELP_TOPICS.forEach((topic) => {
      const title = topic.title.toLowerCase();
      const summary = topic.summary.toLowerCase();
      const cat = topic.category.toLowerCase();
      const kw = topic.keywords.map((k) => k.toLowerCase());

      let score = 0;
      let matchReason: SearchResultItem['matchReason'] = 'category';

      if (!hasQuery) {
        score += 35;
        matchReason = 'popularity';
      } else {
        if (title.includes(q)) {
          score += 310;
          matchReason = 'exact';
        } else if (kw.some((k) => k === q || q.includes(k) || k.includes(q))) {
          score += 240;
          matchReason = 'alias';
        } else if (summary.includes(q) || cat.includes(q)) {
          score += 130;
          matchReason = 'category';
        }
      }

      if (score > 0 || !hasQuery) {
        allResults.push({
          id: `help_${topic.id}`,
          group: 'Help',
          title: topic.title,
          subtitle: topic.summary,
          categoryTag: topic.category,
          iconName: 'HelpCircle',
          score,
          matchReason,
          action: {
            type: 'view',
            targetId: topic.viewTarget
          }
        });
      }
    });

    // 7. SEARCH DEVELOPER DOCUMENTATION
    DEVELOPER_DOC_ITEMS.forEach((doc) => {
      const title = doc.title.toLowerCase();
      const summary = doc.summary.toLowerCase();
      const ep = doc.endpoint.toLowerCase();
      const kw = doc.keywords.map((k) => k.toLowerCase());

      let score = 0;
      let matchReason: SearchResultItem['matchReason'] = 'category';

      if (!hasQuery) {
        score += 30;
        matchReason = 'popularity';
      } else {
        if (ep.includes(q) || title.includes(q)) {
          score += 350;
          matchReason = 'exact';
        } else if (kw.some((k) => k === q || q.includes(k) || k.includes(q))) {
          score += 220;
          matchReason = 'alias';
        } else if (summary.includes(q)) {
          score += 120;
          matchReason = 'category';
        }
      }

      if (score > 0 || !hasQuery) {
        allResults.push({
          id: `dev_${doc.id}`,
          group: 'Developer Documentation',
          title: doc.title,
          subtitle: `${doc.endpoint} • ${doc.summary}`,
          categoryTag: 'API & SDK',
          iconName: 'Code',
          badgeText: doc.method,
          badgeType: doc.method === 'GET' ? 'info' : 'warning',
          score,
          matchReason,
          action: {
            type: 'api',
            targetId: 'developer-portal',
            payload: doc
          }
        });
      }
    });

    // Sort by Score Descending
    const sorted = allResults.sort((a, b) => b.score - a.score);

    // Filter by category group if requested
    const filteredByGroup =
      filterGroup === 'ALL' ? sorted : sorted.filter((item) => item.group === filterGroup);

    // Grouping
    const resultsByGroup: Record<SearchCategoryGroup, SearchResultItem[]> = {
      'Tools': [],
      'Currencies': [],
      'Guides': [],
      'Countries / Markets': [],
      'Help': [],
      'Developer Documentation': []
    };

    filteredByGroup.forEach((item) => {
      resultsByGroup[item.group].push(item);
    });

    return {
      resultsByGroup,
      flatRankedResults: filteredByGroup,
      totalCount: filteredByGroup.length,
      hasQuery,
      directAnswer
    };
  }

  /**
   * Deterministic Natural Language Quick Calculation Engine
   * e.g. "100 USD to EUR", "15% of 250", "$85,000 salary", "20% tip on $85"
   */
  private evaluateDirectFormula(query: string, regionCode: string) {
    const q = query.trim().toLowerCase();

    // 1. Direct FX Convert: e.g. "100 usd to eur", "500 euro in gbp", "1000 xof usd"
    const fxRegex = /(?:convert\s+)?(\d+(?:\.\d+)?)\s*([a-zA-Z]{3}|\$|€|£|¥)\s*(?:to|in|->|into|\/)?\s*([a-zA-Z]{3}|\$|€|£|¥)/i;
    const fxMatch = q.match(fxRegex);
    if (fxMatch) {
      const amount = parseFloat(fxMatch[1]);
      let fromCurr = this.normalizeCurrencySymbol(fxMatch[2].toUpperCase());
      let toCurr = this.normalizeCurrencySymbol(fxMatch[3].toUpperCase());

      if (fromCurr && toCurr && fromCurr !== toCurr) {
        // Approximate mock rate matrix for quick instant answer preview
        const baseRate = this.getApproximateRate(fromCurr, toCurr);
        const converted = (amount * baseRate).toFixed(2);
        return {
          type: 'fx_quick_convert' as const,
          title: `Instant Currency Conversion (${fromCurr} → ${toCurr})`,
          value: `${converted} ${toCurr}`,
          detail: `${amount.toLocaleString()} ${fromCurr} at estimated mid-market rate (1 ${fromCurr} ≈ ${baseRate.toFixed(4)} ${toCurr})`,
          actionToolId: 'currency-converter'
        };
      }
    }

    // 2. Percentage calculation: e.g. "15% of 250", "20 percent of 80", "15% discount on 120"
    const pctRegex = /(\d+(?:\.\d+)?)\s*(?:%|percent)\s*(?:of|on)\s*(?:\$|€|£)?\s*(\d+(?:\.\d+)?)/i;
    const pctMatch = q.match(pctRegex);
    if (pctMatch) {
      const pct = parseFloat(pctMatch[1]);
      const base = parseFloat(pctMatch[2]);
      const result = ((pct / 100) * base).toFixed(2);
      const increased = (base + (pct / 100) * base).toFixed(2);
      const decreased = (base - (pct / 100) * base).toFixed(2);
      return {
        type: 'percentage' as const,
        title: `Instant Percentage: ${pct}% of ${base}`,
        value: `${result}`,
        detail: `Increase: ${increased} • Decrease: ${decreased}`,
        actionToolId: 'percentage-calculator'
      };
    }

    // 3. Tip calculation: e.g. "20% tip on 85", "tip for 120", "split bill 200 4 people"
    const tipRegex = /(?:tip|split)\s*(?:of|for|on)?\s*(\d+(?:\.\d+)?)/i;
    const tipMatch = q.match(tipRegex);
    if (tipMatch && !pctMatch) {
      const bill = parseFloat(tipMatch[1]);
      const tip15 = (bill * 0.15).toFixed(2);
      const tip20 = (bill * 0.20).toFixed(2);
      return {
        type: 'tip_split' as const,
        title: `Instant Tip Estimate on $${bill}`,
        value: `15%: $${tip15} | 20%: $${tip20}`,
        detail: `Total with 15%: $${(bill + parseFloat(tip15)).toFixed(2)} | Total with 20%: $${(bill + parseFloat(tip20)).toFixed(2)}`,
        actionToolId: 'tip-and-split-bill'
      };
    }

    return undefined;
  }

  private normalizeCurrencySymbol(symbolOrCode: string): string {
    const map: Record<string, string> = {
      '$': 'USD',
      '€': 'EUR',
      '£': 'GBP',
      '¥': 'JPY',
      'USD': 'USD',
      'EUR': 'EUR',
      'GBP': 'GBP',
      'CAD': 'CAD',
      'AUD': 'AUD',
      'CHF': 'CHF',
      'XOF': 'XOF',
      'INR': 'INR',
      'JPY': 'JPY',
      'CNY': 'CNY'
    };
    return map[symbolOrCode] || symbolOrCode;
  }

  private getApproximateRate(from: string, to: string): number {
    const usdRates: Record<string, number> = {
      USD: 1.0,
      EUR: 0.9234,
      GBP: 0.7912,
      CAD: 1.365,
      AUD: 1.528,
      CHF: 0.884,
      XOF: 605.5,
      INR: 83.45,
      JPY: 154.2,
      CNY: 7.24
    };
    const rateFrom = usdRates[from] || 1.0;
    const rateTo = usdRates[to] || 1.0;
    return rateTo / rateFrom;
  }

  private getToolAliases(toolId: string): string[] {
    const aliasMap: Record<string, string[]> = {
      'currency-converter': ['fx', 'exchange', 'forex', 'rate', 'dollar', 'euro', 'pound', 'convert money'],
      'salary-calculator': ['paycheck', 'income tax', 'take home', 'gross to net', 'w-2', 'pay', 'wage', 'payroll'],
      'mortgage-calculator': ['home loan', 'amortization', 'monthly mortgage', 'interest rate', 'piti', 'housing'],
      'compare-transfers': ['remit', 'send money', 'wire', 'western union', 'wise', 'remitly', 'transfer fee'],
      'vat-sales-tax': ['vat', 'tva', 'gst', 'sales tax', 'tax rate', 'iva', 'duty'],
      'percentage-calculator': ['percent', 'proportion', 'ratio', 'margin', 'percentage change'],
      'loan-calculator': ['personal loan', 'borrow', 'apr', 'repayment', 'credit'],
      'tip-and-split-bill': ['tip', 'gratuity', 'split check', 'dinner split', 'restaurant tip'],
      'discount-calculator': ['sale', 'markdown', 'coupon', 'savings', 'promo price'],
      'inflation-calculator': ['purchasing power', 'cpi', 'real value', 'historical price'],
      'crypto-profit-calculator': ['bitcoin', 'crypto', 'roi', 'ethereum', 'capital gains'],
      'freelance-rate-calculator': ['hourly rate', 'day rate', 'consultant billing', 'freelance pricing']
    };
    return aliasMap[toolId] || [];
  }
}

export const publicSearchEngine = new PublicSearchEngine();
